import { cloneElement, useEffect, useId, useState } from 'react'
import { cn } from '../../lib/cn'

interface TooltipProps {
  content: string
  /** Single element child; it receives aria-describedby while the tip is open. */
  children: React.ReactElement<{ 'aria-describedby'?: string }>
  className?: string
}

/**
 * Small hover/focus hint. No portal — keep out of overflow-hidden ancestors.
 * aria-describedby is cloned onto the child itself so assistive tech announces
 * it from the focused element (a wrapper span's describedby is not inherited);
 * the span only handles positioning and hover/focus events.
 */
export function Tooltip({ content, children, className }: TooltipProps) {
  const id = useId()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  return (
    <span
      className={cn('relative inline-flex', className)}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      {cloneElement(children, { 'aria-describedby': open ? id : undefined })}
      {open && (
        <span
          role="tooltip"
          id={id}
          className={cn(
            'pointer-events-none absolute bottom-full left-1/2 z-40 mb-1.5 -translate-x-1/2',
            'whitespace-nowrap rounded-control bg-surface-raised px-2.5 py-1 text-xs text-text',
            'ring-1 ring-border-warm shadow-warm-sm',
          )}
        >
          {content}
        </span>
      )}
    </span>
  )
}
